import React, { useCallback, useState } from 'react';
import { Check, Copy, Download, FlaskConical, Loader2, X } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useDebugMode } from '../../../contexts/DebugModeContext';
import { apiService } from '../../../services/ApiService';
import { buildQaCaseMarkdown, qaCaseFileName, TrailClick } from '../../../utils/qaCaseMarkdown';
import { Message } from './types';

/**
 * Admin + debug-mode only: turn this conversation into a Cata Backoffice QA test
 * case. Shows the generated markdown (editable), then Download .md / Copy; the
 * file is imported in the backoffice under QA → Test cases → Import .md.
 * Recorded clicks are included when the click trail is on for the org.
 */
/** `context` (e.g. the vehicle) goes into the case's notes; the title comes from what the user asked. */
const QaCaseExport: React.FC<{ messages: Message[]; orderId?: number | null; context?: string | null }> = ({ messages, orderId, context }) => {
  const { currentUser } = useAuth();
  const { debugMode } = useDebugMode();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [markdown, setMarkdown] = useState('');
  const [copied, setCopied] = useState(false);

  const build = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setCopied(false);
    let clicks: TrailClick[] = [];
    if (orderId) {
      try {
        const res = await apiService.get(`/api/user/orders/${orderId}/clicks`);
        clicks = res.data?.events || [];
      } catch {
        /* the click trail is optional: build from the messages alone */
      }
    }
    const source = [orderId ? `order ${orderId}` : '', context || ''].filter(Boolean).join(' · ') || null;
    setMarkdown(buildQaCaseMarkdown({ messages, clicks, source }));
    setLoading(false);
  }, [messages, orderId, context]);

  const download = () => {
    const url = URL.createObjectURL(new Blob([markdown], { type: 'text/markdown;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = qaCaseFileName(markdown);
    a.click();
    URL.revokeObjectURL(url);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked: the text is still selectable in the box */
    }
  };

  if (currentUser?.role !== 'admin' || !debugMode) return null;

  return (
    <>
      <button
        type="button"
        onClick={build}
        data-track="qa-export"
        title="Admin only — export this conversation as a QA test case for the backoffice"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#efebe2] bg-[#f6f4f0] text-[#999999] hover:bg-[#efebe2] hover:text-[#201e1a] text-xs font-medium flex-shrink-0 cursor-pointer"
      >
        <FlaskConical className="w-3.5 h-3.5" />
        Test case
      </button>

      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#efebe2]">
              <FlaskConical className="w-4 h-4 text-[#201e1a]" />
              <span className="font-semibold text-sm text-[#201e1a]">QA test case from this chat</span>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="ml-auto text-[#999999] hover:text-[#201e1a]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-4 pt-3 text-xs text-[#6B6862]">
              Review the expected results (they describe what the assistant replied this time), then import the file in the
              backoffice: QA → Test cases → Import .md.
            </div>
            <div className="p-4 flex-1 min-h-0 flex">
              {loading ? (
                <div className="flex items-center gap-2 text-[#6B6862] text-sm"><Loader2 className="w-4 h-4 animate-spin" /> building…</div>
              ) : (
                <textarea
                  value={markdown}
                  onChange={e => setMarkdown(e.target.value)}
                  spellCheck={false}
                  aria-label="Test case markdown"
                  className="w-full min-h-[320px] flex-1 font-mono text-xs text-[#201e1a] border border-[#efebe2] rounded-lg p-3 resize-none focus:outline-none focus:border-[#6866ff]"
                />
              )}
            </div>
            <div className="flex items-center gap-2 px-4 py-3 border-t border-[#efebe2]">
              <button
                type="button"
                onClick={download}
                disabled={loading || !markdown}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#201e1a] text-white text-xs font-medium hover:bg-black disabled:opacity-50"
              >
                <Download className="w-3.5 h-3.5" /> Download .md
              </button>
              <button
                type="button"
                onClick={copy}
                disabled={loading || !markdown}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#efebe2] text-[#201e1a] text-xs font-medium hover:bg-[#f6f4f0] disabled:opacity-50"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QaCaseExport;
